(() => {
  'use strict';

  /* Multiple shalin-* sections can be stacked on the same page (each loads
     this script via its own <script defer> tag), so without this guard the
     whole IIFE — and every document-level event listener below — would
     run once per section, firing handlers multiple times per click. */
  if (window.__shalinThemeInitialized) return;
  window.__shalinThemeInitialized = true;

  const drawer = document.querySelector('[data-shalin-cart-drawer]');
  const drawerBody = document.querySelector('[data-shalin-cart-body]');
  const header = document.querySelector('[data-shalin-header]');
  const countdowns = document.querySelectorAll('[data-shalin-countdown]');
  const cartCountEls = document.querySelectorAll('[data-shalin-cart-count]');

  /* ---------- Cart drawer open/close ---------- */
  const setDrawer = (open) => {
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('shalin-cart-drawer-open', open);
    if (open) {
      const closeBtn = drawer.querySelector('[data-shalin-cart-close]');
      if (closeBtn) closeBtn.focus();
    }
  };

  /* ---------- AJAX cart: add / change / remove, refreshed via Section Rendering API ----------
     The cart drawer lives inside the Shalin header section (no separate global
     section needed, so no layout.liquid edit is required). Its Section
     Rendering API id is whatever Shopify assigned that header instance at
     runtime, read from a data attribute rather than hardcoded. */
  const headerScope = document.querySelector('[data-shalin-header-section-id]');
  const CART_SECTION_ID = headerScope ? headerScope.dataset.shalinHeaderSectionId : null;

  /**
   * Refreshes the drawer's markup via the Section Rendering API and derives
   * the cart's total item count from the refreshed lines — avoiding a
   * separate /cart.js round trip just to read item_count (that used to make
   * every add-to-cart wait on three sequential fetches before anything
   * visible happened). Returns the item count, or null on failure.
   */
  async function refreshCartDrawer() {
    if (!drawerBody || !CART_SECTION_ID) return null;
    try {
      // cache: 'no-store' + a cache-busting param: this request must always
      // hit the server fresh. Without it, a browser or intermediate cache
      // can serve back an earlier response for this exact URL (e.g. the
      // pre-add "empty cart" render) — which reads as "added but the drawer
      // doesn't show it until I reload the page".
      const res = await fetch(`${window.location.pathname}?sections=${CART_SECTION_ID}&_=${Date.now()}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) return null;
      const data = await res.json();
      const html = data[CART_SECTION_ID];
      if (!html) return null;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newBody = parsed.querySelector('[data-shalin-cart-body]');
      const newFooter = parsed.querySelector('[data-shalin-cart-footer]');
      const currentFooter = document.querySelector('[data-shalin-cart-footer]');
      if (newBody) drawerBody.innerHTML = newBody.innerHTML;
      if (newFooter && currentFooter) currentFooter.outerHTML = newFooter.outerHTML;

      let itemCount = 0;
      drawerBody.querySelectorAll('[data-shalin-cart-line]').forEach((line) => {
        itemCount += Number(line.dataset.shalinQuantity) || 0;
      });
      return itemCount;
    } catch (error) {
      console.error('[shalin] cart drawer refresh failed:', error);
      return null;
    }
  }

  function updateCartCount(count) {
    cartCountEls.forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  /* Adding to cart is handled by Tinker's own <product-form-component>
     (see sections/shalin-best-sellers.liquid), which dispatches
     `shopify:cart:lines-update` on success. Tinker's native header, cart
     drawer and cart-icon bubble already listen for that event; this keeps
     the Shalin header's drawer/bubble in sync too when that header is used. */
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (!drawer) return;
    const promise = event.promise || Promise.resolve({ detail: {} });
    promise
      .then(async ({ detail } = {}) => {
        if (detail?.didError) return;
        const itemCount = await refreshCartDrawer();
        if (itemCount !== null) updateCartCount(itemCount);
        if (event.action === 'add') setDrawer(true);
      })
      .catch(() => {});
  });

  async function changeCartLine(line, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
        cache: 'no-store'
      });
      const itemCount = await refreshCartDrawer();
      if (itemCount !== null) updateCartCount(itemCount);
    } catch (error) {
      console.error('[shalin] cart line update failed:', error);
      window.location.reload();
    }
  }

  /* ---------- Wishlist (client-side only: no wishlist app/backend wired up yet) ---------- */
  const WISHLIST_KEY = 'shalin:wishlist';
  const getWishlist = () => {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (error) {
      return [];
    }
  };
  const setWishlistButtonState = (button) => {
    const id = button.dataset.shalinWishlist;
    const saved = getWishlist().includes(id);
    button.setAttribute('aria-pressed', String(saved));
  };
  function toggleWishlist(button) {
    const id = button.dataset.shalinWishlist;
    const list = getWishlist();
    const index = list.indexOf(id);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(id);
    }
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    setWishlistButtonState(button);
  }
  document.querySelectorAll('[data-shalin-wishlist]').forEach(setWishlistButtonState);

  /* ---------- Announcement bar dismiss ---------- */
  function initAnnouncementDismiss() {
    const bar = document.querySelector('[data-shalin-announcement]');
    if (!bar) return;
    const key = `shalin:announcement-dismissed:${bar.dataset.shalinAnnouncement || 'default'}`;
    if (localStorage.getItem(key)) {
      bar.hidden = true;
      updateHeaderOffset();
      return;
    }
    const closeButton = bar.querySelector('[data-shalin-announcement-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        localStorage.setItem(key, '1');
        bar.hidden = true;
        updateHeaderOffset();
      });
    }
  }

  /* ---------- Header offset ----------
     The header and announcement bar are separate sibling sections (not
     nested), so the header can't rely on normal document flow to sit below
     the announcement bar while still floating transparently over the hero.
     Instead we measure the announcement bar's real height and expose it as
     a CSS variable the header's `top` reads (see shalin-theme.css). Runs on
     load, on resize (text can wrap differently), and after dismissal. */
  function updateHeaderOffset() {
    const announcement = document.querySelector('[data-shalin-announcement]');
    const height = announcement && !announcement.hidden ? announcement.offsetHeight : 0;
    document.documentElement.style.setProperty('--shalin-header-offset', `${height}px`);
  }

  /* ---------- Hero slider (dots + autoplay, pauses on interaction) ---------- */
  function initHeroSlider() {
    const hero = document.querySelector('[data-shalin-hero]');
    if (!hero) return;
    const slides = Array.from(hero.querySelectorAll('[data-shalin-hero-slide]'));
    if (slides.length <= 1) return;
    const dotsWrap = hero.querySelector('[data-shalin-hero-dots]');
    let current = 0;
    let timer;

    const show = (index) => {
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      if (dotsWrap) {
        Array.from(dotsWrap.children).forEach((dot, i) =>
          dot.setAttribute('aria-current', String(i === index))
        );
      }
      current = index;
    };

    const next = () => show((current + 1) % slides.length);

    const play = () => {
      stop();
      timer = window.setInterval(next, 6000);
    };
    const stop = () => window.clearInterval(timer);

    if (dotsWrap) {
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show slide ${i + 1}`);
        dot.addEventListener('click', () => {
          show(i);
          play();
        });
        dotsWrap.appendChild(dot);
      });
    }

    show(0);
    play();
    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', play);
    hero.addEventListener('touchstart', stop, { passive: true });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) stop();
  }

  /* ---------- Delegated click/submit handling ---------- */
  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-shalin-cart-open]');
    const closeButton = event.target.closest('[data-shalin-cart-close]');
    const menuButton = event.target.closest('[data-shalin-mobile-menu]');
    const searchButton = event.target.closest('[data-shalin-mobile-search-toggle]');
    const wishlistButton = event.target.closest('[data-shalin-wishlist]');
    const qtyButton = event.target.closest('[data-shalin-qty]');
    const removeButton = event.target.closest('[data-shalin-remove]');
    const menu = document.querySelector('[data-shalin-mobile-nav]');
    const mobileSearch = document.querySelector('[data-shalin-mobile-search]');

    if (openButton) {
      event.preventDefault();
      setDrawer(true);
    }
    if (closeButton) {
      event.preventDefault();
      setDrawer(false);
    }
    if (menuButton && menu) {
      const open = menu.classList.toggle('is-open');
      menuButton.setAttribute('aria-expanded', String(open));
      if (mobileSearch) mobileSearch.classList.remove('is-open');
    }
    if (searchButton && mobileSearch) {
      const open = mobileSearch.classList.toggle('is-open');
      searchButton.setAttribute('aria-expanded', String(open));
      if (menu) menu.classList.remove('is-open');
      if (open) {
        const input = mobileSearch.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    }
    if (wishlistButton) {
      event.preventDefault();
      toggleWishlist(wishlistButton);
    }
    if (qtyButton) {
      event.preventDefault();
      const line = Number(qtyButton.dataset.shalinLine);
      const delta = Number(qtyButton.dataset.shalinQty);
      const currentQty = Number(qtyButton.closest('[data-shalin-cart-line]')?.dataset.shalinQuantity || 1);
      changeCartLine(line, Math.max(0, currentQty + delta));
    }
    if (removeButton) {
      event.preventDefault();
      const line = Number(removeButton.dataset.shalinLine);
      changeCartLine(line, 0);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setDrawer(false);
      const menu = document.querySelector('[data-shalin-mobile-nav]');
      if (menu) menu.classList.remove('is-open');
      const mobileSearch = document.querySelector('[data-shalin-mobile-search]');
      if (mobileSearch) mobileSearch.classList.remove('is-open');
    }
  });

  if (header) {
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  countdowns.forEach((countdown) => {
    const target = new Date(countdown.dataset.shalinCountdown || '').getTime();
    if (!target || Number.isNaN(target)) return;
    const update = () => {
      const distance = Math.max(0, target - Date.now());
      const values = {
        days: Math.floor(distance / 86400000),
        hours: Math.floor((distance % 86400000) / 3600000),
        minutes: Math.floor((distance % 3600000) / 60000),
        seconds: Math.floor((distance % 60000) / 1000)
      };
      Object.entries(values).forEach(([key, value]) => {
        const node = countdown.querySelector(`[data-shalin-countdown-${key}]`);
        if (node) node.textContent = String(value).padStart(2, '0');
      });
    };
    update();
    window.setInterval(update, 1000);
  });

  initAnnouncementDismiss();
  updateHeaderOffset();
  window.addEventListener('resize', updateHeaderOffset);
  window.addEventListener('load', updateHeaderOffset);
  initHeroSlider();
})();
